import { zodResolver } from "@hookform/resolvers/zod";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFieldArray, type Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  useGetSeiIntegration,
  useUpdateSeiIntegration,
} from "../../../api/bpm/endpoints";
import type { OrganizationSeiIntegrationDto } from "../../../api/bpm/model";
import { useAuth } from "../../../auth";
import { FormTextField } from "../../../forms/FormTextField";
import { usePageTitle } from "../../../contexts/PageTitleContext";
import { decodeNavParams } from "../../../utils/navParams";

const seiIntegrationSearchSchema = z.object({
  fromParams: z.string().optional(),
});

const createSeiIntegrationSchema = (t: (key: string) => string) =>
  z.object({
    seiUrl: z.string(),
    sigla: z.string(),
    accessKey: z.string(),
    units: z.array(z.object({
      id: z.string().min(1, t("common.required") || "Obrigatório"),
      label: z.string().min(1, t("common.required") || "Obrigatório"),
    })).optional(),
    series: z.array(z.object({
      id: z.string().min(1, t("common.required") || "Obrigatório"),
      label: z.string().min(1, t("common.required") || "Obrigatório"),
    })).optional(),
  });

function ListEditor({
  name,
  title,
  control,
  isBusy,
}: {
  name: "units" | "series";
  title: string;
  control: Control<OrganizationSeiIntegrationDto>;
  isBusy: boolean;
}) {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        <Button
          startIcon={<AddIcon />}
          size="small"
          onClick={() => append({ id: "", label: "" })}
          disabled={isBusy}
        >
          {t("common.add")}
        </Button>
      </Stack>
      <Stack spacing={2}>
        {fields.map((field, index) => (
          <Stack direction="row" spacing={2} key={field.id} alignItems="flex-start">
            <FormTextField
              name={`${name}.${index}.id`}
              label="ID"
              fullWidth
              variant="standard"
              disabled={isBusy}
            />
            <FormTextField
              name={`${name}.${index}.label`}
              label="Label"
              fullWidth
              variant="standard"
              disabled={isBusy}
            />
            <IconButton onClick={() => remove(index)} disabled={isBusy} sx={{ mt: 1 }}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        ))}
        {fields.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t("common.noItems", { defaultValue: "Nenhum item adicionado" })}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export const Route = createFileRoute(
  "/__auth/admin/organizations/$organizationKey/sei-integration",
)({
  component: OrganizationSeiIntegrationComponent,
  validateSearch: (search) => seiIntegrationSearchSchema.parse(search),
  staticData: {
    title: "routes.organizationSeiIntegration",
  },
});

function OrganizationSeiIntegrationComponent() {
  const { t } = useTranslation();
  const { currentTenant } = useAuth();
  const { organizationKey } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { setTitlePrefix } = usePageTitle();
  const queryClient = useQueryClient();
  const tenant = currentTenant || "default-domain";

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const schema = useMemo(() => createSeiIntegrationSchema(t), [t]);
  const methods = useForm<OrganizationSeiIntegrationDto>({
    resolver: zodResolver(schema) as any,
    defaultValues: {
      sigla: "",
      seiUrl: "",
      accessKey: "",
      units: [],
      series: [],
    },
  });

  const { handleSubmit, reset, control } = methods;

  const {
    data,
    isLoading,
    error,
  } = useGetSeiIntegration(tenant, organizationKey, {});

  const mutation = useUpdateSeiIntegration({
    mutation: {
      onSuccess: (response, variables) => {
        reset((response as any)?.data || response || variables.data);
        setSnackbar({
          open: true,
          severity: "success",
          message: t("admin.organizations.seiIntegration.saveSuccess"),
        });
        void queryClient.invalidateQueries({
          queryKey: ["getSeiIntegration", tenant, organizationKey],
        });
      },
      onError: (err: unknown) => {
        setSnackbar({
          open: true,
          severity: "error",
          message:
            (err as { response?: { data?: { message?: string } } })?.response
              ?.data?.message ?? t("admin.organizations.seiIntegration.saveError"),
        });
      },
    },
  });

  useEffect(() => {
    setTitlePrefix(organizationKey);
    return () => setTitlePrefix(null);
  }, [organizationKey, setTitlePrefix]);

  useEffect(() => {
    if (data) {
      reset((data as any)?.data || data);
    }
  }, [data, reset]);

  const handleBack = () => {
    const previousParams = search.fromParams
      ? decodeNavParams(search.fromParams) || {}
      : {};

    navigate({ to: "/admin/organizations", search: previousParams as never });
  };

  const isSaving = mutation.isPending;
  const isBusy = isLoading || isSaving;

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", width: "100%", py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton
          onClick={handleBack}
          color="primary"
          aria-label={t("common.back")}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
          >
            {t("admin.organizations.seiIntegration.title")}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t("admin.organizations.seiIntegration.subtitle")}
          </Typography>
        </Box>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t("admin.organizations.seiIntegration.loadError")}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          boxShadow: (theme) => theme.shadows[2],
          position: "relative",
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={(event) =>
                void handleSubmit((values) =>
                  mutation.mutate({ tenant, organizationKey, data: values })
                )(event)
              }
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "action.selected",
                    color: "primary.main",
                    width: 48,
                    height: 48,
                  }}
                >
                  <IntegrationInstructionsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {t("admin.organizations.seiIntegration.connectionData")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {organizationKey}
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={2}>
                <FormTextField
                  name="seiUrl"
                  label={t("admin.organizations.seiIntegration.seiUrl")}
                  fullWidth
                  variant="standard"
                  disabled={isBusy}
                />

                <FormTextField
                  name="sigla"
                  label={t("admin.organizations.seiIntegration.acronym")}
                  fullWidth
                  variant="standard"
                  disabled={isBusy}
                />

                <FormTextField
                  name="accessKey"
                  label={t("admin.organizations.seiIntegration.accessKey")}
                  fullWidth
                  variant="standard"
                  disabled={isBusy}
                />

                <Divider sx={{ my: 2 }} />

                <ListEditor
                  name="units"
                  title={t("admin.organizations.seiIntegration.units", "Unidades")}
                  control={control}
                  isBusy={isBusy}
                />

                <Divider sx={{ my: 2 }} />

                <ListEditor
                  name="series"
                  title={t("admin.organizations.seiIntegration.series", "Séries (Domínio)")}
                  control={control}
                  isBusy={isBusy}
                />
              </Stack>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleBack} disabled={isBusy}>
                  {t("common.cancel")}
                </Button>
                <Button type="submit" variant="contained" disabled={isBusy}>
                  {isSaving ? t("common.saving") : t("common.save")}
                </Button>
              </Stack>
            </Box>
          </FormProvider>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
